import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { ErrorReasons } from '@shared/constants/errors.constants'
import { Workspace } from '@shared/types/workspaces.types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAuthStore } from '../../hooks/useAuthStore'
import { useDeleteWorkspaceUserMutation } from '../../hooks/useMutations/useWorkspacesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

const dialogStyles = {
  '& .MuiDialog-paper': {
    width: '32.5rem',
    padding: '1.5rem',
    borderRadius: '.25rem'
  }
}

const dialogTitleStyles = {
  py: '2rem'
}

const dialogContentStyles = {
  padding: '1.5rem'
}

export const AbandonWorkspaceModal = NiceModal.create(
  ({ companyId, workspace }: { companyId: string; workspace: Workspace }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const { userId } = useAuthStore() as { userId: string }
    const [isLastAdmin, setIsLastAdmin] = useState(false)
    const { showSnackbar } = useSnackbar()

    const deleteWorkspaceUserMutation = useDeleteWorkspaceUserMutation(
      companyId,
      workspace.id
    )

    return (
      <>
        {!isLastAdmin ? (
          <Dialog
            open={modal.visible}
            onClose={() => modal.remove()}
            sx={dialogStyles}
          >
            <DialogTitle sx={dialogTitleStyles}>
              <Typography variant='h6' component='span'>
                {t('abandon_workspace_modal.title_admin', {
                  workspaceName: workspace.name
                })}
              </Typography>
            </DialogTitle>
            <DialogContent sx={dialogContentStyles}>
              <Typography variant='body1'>
                {t('abandon_workspace_modal.text_admin')}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: '.5rem' }}>
              <Button
                variant='outlined'
                sx={{
                  border: '1px solid rgba(121, 134, 203, 0.60)',
                  color: '#9FA8DA',
                  background: 'rgba(178, 223, 219, 0.10)',
                  textTransform: 'capitalize'
                }}
                onClick={() => modal.remove()}
              >
                {t('common.cancel')}
              </Button>
              <LoaderButton
                variant='contained'
                loading={deleteWorkspaceUserMutation.isPending}
                disabled={deleteWorkspaceUserMutation.isPending}
                label={t('common.abandon')}
                color='error'
                sx={{ textTransform: 'none', width: '6.875rem' }}
                onClick={() =>
                  deleteWorkspaceUserMutation.mutate(
                    { companyId, workspaceId: workspace.id, userId: userId },
                    {
                      onSuccess: () => {
                        showSnackbar({
                          message: t('abandon_workspace_modal.success', {
                            workspaceName: workspace.name
                          }),
                          severity: 'success'
                        })
                        modal.remove()
                      },
                      onError: (error: any) => {
                        if (
                          error.response.data.data.reason ===
                          ErrorReasons.WORKSPACE_USER_CANNOT_DELETE_LAST_ADMIN
                        ) {
                          setIsLastAdmin(true)
                        } else {
                          showSnackbar({
                            message: t('abandon_workspace_modal.error'),
                            severity: 'error'
                          })
                          modal.remove()
                        }
                      }
                    }
                  )
                }
              />
            </DialogActions>
          </Dialog>
        ) : (
          <Dialog
            open={modal.visible}
            onClose={() => modal.remove()}
            sx={dialogStyles}
          >
            <DialogTitle sx={dialogTitleStyles}>
              {t('abandon_workspace_modal.title')}
            </DialogTitle>
            <DialogContent sx={dialogContentStyles}>
              <Typography variant='body1'>
                {t('abandon_workspace_modal.text')}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant='outlined'
                color='primary'
                sx={{
                  border: '1px solid rgba(121, 134, 203, 0.60)',
                  color: '#9FA8DA',
                  background: 'rgba(178, 223, 219, 0.10)',
                  textTransform: 'capitalize'
                }}
                onClick={() => modal.remove()}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                sx={{ textTransform: 'none' }}
                component={Link}
                to={`/companies/${companyId}/workspaces/${workspace.id}/members`}
                onClick={() => modal.remove()}
              >
                {t('abandon_workspace_modal.assign_admin')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  }
)
